import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Action labels in Portuguese
const ACTION_LABELS = {
  create: "Criar",
  update: "Atualizar",
  delete: "Eliminar",
  upload: "Upload",
  download: "Download",
  login: "Login",
  logout: "Logout",
  enrich: "Enriquecer",
  inactivate: "Inativar",
  approve: "Aprovar",
  reject: "Rejeitar",
  scan: "Analisar"
};

// Entity type labels in Portuguese
const ENTITY_LABELS = {
  client: "Cliente",
  supplier: "Fornecedor",
  employee: "Colaborador",
  other: "KYU",
  conflict: "Conflito",
  offer: "Oferta",
  document: "Documento",
  user: "Utilizador",
  tenant: "Tenant",
  report: "Relatório"
};

// Action colors
const ACTION_COLORS = {
  create: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  update: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  delete: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  upload: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  download: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  login: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  logout: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
  approve: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  reject: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
};

export const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    user_id: ''
  });
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let params = [`limit=${limit}`, `skip=${page * limit}`];
      if (filters.entity_type) params.push(`entity_type=${filters.entity_type}`);
      if (filters.action) params.push(`action=${filters.action}`);
      if (filters.user_id) params.push(`user_id=${filters.user_id}`);
      
      const response = await api.get(`/audit-logs?${params.join('&')}`);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/audit-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const getActionBadge = (action) => {
    const colorClass = ACTION_COLORS[action] || "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400";
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${colorClass}`}>
        {ACTION_LABELS[action] || action}
      </span>
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div data-testid="audit-log-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Log de Auditoria</h1>
          <p className="text-muted-foreground">Registo de todas as ações realizadas no sistema</p>
        </div>
        <button
          onClick={() => { fetchLogs(); fetchStats(); }}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Atualizar
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl border bg-card">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span className="text-sm text-muted-foreground">Total de Registos</span>
            </div>
            <p className="text-2xl font-bold mt-1">{stats.total_logs?.toLocaleString()}</p>
          </div>
          <div className="p-4 rounded-xl border bg-card">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-muted-foreground">Hoje</span>
            </div>
            <p className="text-2xl font-bold mt-1">{stats.today_count?.toLocaleString()}</p>
          </div>
          <div className="p-4 rounded-xl border bg-card">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="text-sm text-muted-foreground">Criações</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-green-500">{stats.by_action?.create?.toLocaleString() || 0}</p>
          </div>
          <div className="p-4 rounded-xl border bg-card">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="text-sm text-muted-foreground">Eliminações</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-red-500">{stats.by_action?.delete?.toLocaleString() || 0}</p>
          </div>
        </div>
      )}

      {/* Activity by Type */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border bg-card">
            <h3 className="font-semibold mb-3">Atividade por Tipo de Entidade</h3>
            <div className="space-y-2">
              {Object.entries(stats.by_entity || {}).slice(0, 6).map(([entity, count]) => (
                <div key={entity} className="flex items-center justify-between">
                  <span className="text-sm">{ENTITY_LABELS[entity] || entity}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 rounded-full bg-muted overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${Math.min((count / stats.total_logs) * 100 * 5, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-12 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="p-4 rounded-xl border bg-card">
            <h3 className="font-semibold mb-3">Utilizadores Mais Ativos</h3>
            <div className="space-y-2">
              {Object.entries(stats.by_user || {}).slice(0, 6).map(([userId, count]) => (
                <div key={userId} className="flex items-center justify-between">
                  <span className="text-sm truncate max-w-[150px]">{userId}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 rounded-full bg-muted overflow-hidden">
                      <div 
                        className="h-full bg-purple-500 rounded-full"
                        style={{ width: `${Math.min((count / stats.total_logs) * 100 * 5, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-12 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 p-4 rounded-xl border bg-card">
        <div>
          <label className="text-sm font-medium mr-2">Tipo de Entidade:</label>
          <select
            value={filters.entity_type}
            onChange={(e) => handleFilterChange('entity_type', e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-input bg-background text-sm"
          >
            <option value="">Todos</option>
            <option value="client">Cliente</option>
            <option value="supplier">Fornecedor</option>
            <option value="employee">Colaborador</option>
            <option value="other">KYU</option>
            <option value="conflict">Conflito</option>
            <option value="offer">Oferta</option>
            <option value="document">Documento</option>
            <option value="user">Utilizador</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium mr-2">Ação:</label>
          <select
            value={filters.action}
            onChange={(e) => handleFilterChange('action', e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-input bg-background text-sm"
          >
            <option value="">Todas</option>
            <option value="create">Criar</option>
            <option value="update">Atualizar</option>
            <option value="delete">Eliminar</option>
            <option value="upload">Upload</option>
            <option value="download">Download</option>
            <option value="approve">Aprovar</option>
            <option value="reject">Rejeitar</option>
          </select>
        </div>
        {(filters.entity_type || filters.action) && (
          <button
            onClick={() => setFilters({ entity_type: '', action: '', user_id: '' })}
            className="px-3 py-1.5 rounded-lg bg-muted text-sm font-medium hover:bg-muted/80"
          >
            Limpar filtros
          </button>
        )}
        <div className="ml-auto text-sm text-muted-foreground">
          {total.toLocaleString()} registos encontrados
        </div>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border">
          <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-muted-foreground">Nenhum registo de auditoria encontrado</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Ação</th>
                <th>Tipo</th>
                <th>ID da Entidade</th>
                <th>Utilizador</th>
                <th>Tenant</th>
                <th>Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, idx) => (
                <tr key={`${log.log_id || idx}`}>
                  <td className="text-sm">
                    {log.created_at ? new Date(log.created_at).toLocaleString('pt-PT', { 
                      dateStyle: 'short', 
                      timeStyle: 'medium' 
                    }) : '-'}
                  </td>
                  <td>{getActionBadge(log.action)}</td>
                  <td className="text-sm">{ENTITY_LABELS[log.entity_type] || log.entity_type}</td>
                  <td className="text-xs font-mono text-muted-foreground">{log.entity_id?.slice(0, 20) || '-'}</td>
                  <td className="text-sm">{log.user_id?.slice(0, 15) || '-'}</td>
                  <td className="text-xs text-muted-foreground">{log.tenant_id?.slice(0, 15) || 'Global'}</td>
                  <td className="text-xs text-muted-foreground max-w-[200px] truncate">
                    {log.details ? JSON.stringify(log.details).slice(0, 50) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Página {page + 1} de {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 rounded-lg border border-border font-medium hover:bg-muted transition-colors disabled:opacity-50"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 rounded-lg border border-border font-medium hover:bg-muted transition-colors disabled:opacity-50"
            >
              Seguinte
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
